import React, { useState } from 'react';
import { useRequest } from '../../hooks/useRequest';
import { InterruptProcessModal } from '../../components';
import { handleHttpAction } from '../../util/api';
import { useResetState } from './useResetState';

export default Component => ({ api, ...rest }) => {
    const [view, setView] = useState('list'); // list | edit-form | remove-modal
    const [action, setAction] = useState(null); // add | edit | remove | duplicate
    const [ready, setReady] = useState(false);
    const [initialValues, setInitialValues] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [retriggerKey, setRetriggerKey] = useState(null);

    const { error, loading, request } = useRequest();

    useResetState(ready, setReady, 3000);

    const reloadPage = () => setRetriggerKey(new Date().getTime());

    const goBack = callback => {
        setView('list');
        setAction(null);
        setInitialValues(null);
        if (typeof callback === 'function') {
            callback();
        }
    };

    const scrollToNewElement = data => {
        setTimeout(() => {
            /** see InlineAdminEntity */
            const cardElement = document.getElementById(`flip-card-${data.id || data._id}`);

            if (cardElement) {
                cardElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }
        }, 1000);
    };

    const editAction = body => {
        const handler = () =>
            handleHttpAction(`${api}/update`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body,
            });
        request(handler, data => {
            reloadPage();
            setReady(true);

            setTimeout(() => {
                goBack(() => scrollToNewElement(data));
            }, 1000);
        });
    };

    const createAction = body => {
        const handler = () =>
            handleHttpAction(`${api}/add`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body,
            });

        request(handler, data => {
            reloadPage();
            setReady(true);
            setTimeout(() => {
                goBack(() => scrollToNewElement(data));
            }, 1000);
        });
    };

    const deleteAction = () => {
        const { id, _id } = initialValues;
        const handler = () =>
            handleHttpAction(`${api}/${_id || id}/delete`, {
                method: 'DELETE',
            });

        const callback = () => {
            setModalOpen(false);
            reloadPage();
        };

        request(handler, callback, callback);
    };

    const duplicateAction = body => {
        const handler = () =>
            handleHttpAction(`${api}/duplicate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            });

        request(handler, data => {
            reloadPage();
            setTimeout(() => {
                goBack(() => scrollToNewElement(data));
            }, 1000);
        });
    };

    const handleAdminAction = (action, initialValues = null) => {
        setInitialValues(initialValues);

        const isRemoveAction = action === 'remove';
        const isDuplicateAction = action === 'duplicate';

        if (isRemoveAction) return setModalOpen(true);
        if (isDuplicateAction) return duplicateAction(initialValues);

        setAction(action);
        setView('edit-form');
    };

    const handleSubmit = values => {
        const addNewItem = action === 'add';
        const body = JSON.stringify(values);
        const handler = addNewItem ? createAction : editAction;
        handler(body);
    };

    const showList = view === 'list';
    const showForm = view === 'edit-form';

    return (
        <>
            {error && <p style={{ color: 'red' }}>Failed to fetch data: {error}</p>}

            <Component
                error={error}
                showList={showList}
                showForm={showForm}
                action={action}
                modalOpen={modalOpen}
                initialValues={initialValues}
                goBack={goBack}
                handleSubmit={handleSubmit}
                handleAdminAction={handleAdminAction}
                setModalOpen={setModalOpen}
                deleteAction={deleteAction}
                duplicateAction={duplicateAction}
                retriggerKey={retriggerKey}
                loading={loading}
                ready={ready}
                {...rest}
            />
            {modalOpen && (
                <InterruptProcessModal
                    id="remove-superdeal-item"
                    isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    handleInterruption={deleteAction}
                >
                    <InterruptProcessModal.Icon />
                    <InterruptProcessModal.Heading />
                    <InterruptProcessModal.Description>
                        This item will be removed permanently
                    </InterruptProcessModal.Description>
                    <InterruptProcessModal.Footer>
                        <InterruptProcessModal.Button />
                    </InterruptProcessModal.Footer>
                </InterruptProcessModal>
            )}
        </>
    );
};
